import { DOCS_URL } from "../../../config/constants.js";
import {
    EMediaAndTextColumnCount,
    EMediaAndTextCopyAlignment,
    EMediaAndTextLayoutType,
    EMediaAndTextMediaPositionHorizontal,
    EMediaAndTextMediaPositionVertical,
    EMediaAndTextVerticalAlignment,
    ETitleFontSizes,
} from "../../../config/enums.js";
import uploadToMux from "../../../utils/uploadToMux.js";
import uploadToMuxMobile from "../../../utils/uploadToMuxMobile.js";
import MediaAndText from "./MediaAndText.svelte";

export default {
    component: MediaAndText,
    name: "Media and Text",
    description: "Allows images/videos, heading and body copy.",
    docsLink: `${DOCS_URL}/storybook/?path=/docs/molecules-MediaAndText--default`,
    image: "https://unpkg.com/@tabler/icons@3.5.0/icons/outline/photo-scan.svg",
    screenshot:
        "https://cdn.builder.io/api/v1/image/assets%2F5d0546160e954b87b82e2663451afcb1%2Fbad684738e154368939c8a6273d3cf6a",
    inputs: [
        {
            friendlyName: "Layout Type",
            name: "layoutType",
            type: "text",
            enum: Object.values(EMediaAndTextLayoutType),
            defaultValue: EMediaAndTextLayoutType.MT,
        },
        {
            friendlyName: "Layout Columns",
            name: "columns",
            type: "text",
            enum: Object.values(EMediaAndTextColumnCount),
            defaultValue: EMediaAndTextColumnCount.SINGLE,
        },
        {
            // This should only be for layoutType Media Only
            friendlyName: "Full Bleed/Edge-to-Edge?",
            name: "fullBleed",
            type: "boolean",
            defaultValue: false,
            helperText: "Removes side padding/margins.",
            showIf: `options.get('layoutType') === 'Media Only'`,
        },
        {
            // This should only be for layoutType that has text
            friendlyName: "Heading",
            name: "heading",
            type: "text",
            showIf: `options.get('layoutType') !== 'Media Only'`,
        },
        {
            friendlyName: "Heading Level (Semantic)",
            name: "headingLevel",
            type: "text",
            enum: ["h1", "h2", "h3", "h4", "h5", "h6"],
            defaultValue: "h3",
            helperText: "Select the semantic HTML heading level for SEO and accessibility",
            showIf: `options.get('layoutType') !== 'Media Only' && options.get('heading') !== ''`,
        },
        {
            // This should only be for layoutType that has text
            friendlyName: "Heading Font Size",
            name: "titleSize",
            type: "text",
            enum: Object.values(ETitleFontSizes),
            defaultValue: ETitleFontSizes.H3,
            showIf: `options.get('layoutType') !== 'Media Only' && options.get('heading') !== ''`,
        },
        {
            // This should only be for layoutType that has text
            friendlyName: "Description 1",
            name: "description1",
            type: "richText",
            helperText: "Please use formating sparingly.",
            showIf: `options.get('layoutType') !== 'Media Only'`,
        },
        {
            // This should only be for layoutType Text Only
            // This should only be for two column layouts
            friendlyName: "Description 2",
            name: "description2",
            type: "richText",
            helperText: "Please use formating sparingly.",
            showIf: `options.get('layoutType') === 'Text Only' && options.get('columns') === 'Double'`,
        },
        {
            // This should only be for layoutType Media and Text
            // This should only be for two column layouts
            friendlyName: "Sticky Text on Desktop?",
            name: "stickyText",
            type: "boolean",
            defaultValue: false,
            showIf: `options.get('layoutType') === 'Media and Text' && options.get('columns') === 'Double'`,
        },
        {
            // This should only be for layoutType that has text
            friendlyName: "Horizontal Text Alignment",
            name: "copyAlignment",
            type: "text",
            enum: Object.values(EMediaAndTextCopyAlignment),
            defaultValue: EMediaAndTextCopyAlignment.LEFT,
            showIf: `options.get('layoutType') !== 'Media Only'`,
        },
        {
            // This should only be for layoutType Media and Text
            // This should only be for two column layouts
            friendlyName: "Vertical Text Alignment",
            name: "verticalAlignment",
            type: "text",
            enum: Object.values(EMediaAndTextVerticalAlignment),
            defaultValue: EMediaAndTextVerticalAlignment.MIDDLE,
            showIf: `options.get('layoutType') === 'Media and Text' && options.get('columns') === 'Double'`,
        },
        {
            // This should only be for layout type with media
            friendlyName: "Main Image",
            name: "mainImage",
            type: "object",
            helperText: "Any Aspect Ratio. Uploading a video will replace this image.",
            showIf: `options.get('layoutType') !== 'Text Only'`,
            subFields: [
                {
                    friendlyName: "Source",
                    name: "src",
                    type: "file",
                    allowedFileTypes: ["jpg", "webm", "gif", "png"],
                },
                {
                    friendlyName: "Alternate Text",
                    name: "alt",
                    type: "text",
                },
            ],
        },
        {
            // This should only be for layout type with media
            friendlyName: "Main Image Link (optional)",
            name: "mainImageLink",
            type: "text",
            showIf: `options.get('layoutType') !== 'Text Only'`,
        },
        {
            // This should only be for layout type with media
            friendlyName: "Main Video",
            helperText: "Any aspect ratio. This overrides the main image.",
            name: "mainVideoSrc",
            type: "file",
            allowedFileTypes: ["mov", "webm", "mp4"],
            showIf: `options.get('layoutType') !== 'Text Only'`,
            onChange: uploadToMux,
        },
        {
            // This should only be for layout type with media
            friendlyName: "Main Video (Mux)",
            helperText: "Mux Video ID. This overrides the main image.",
            name: "muxId",
            hidden: true,
            //required: false,
            type: "text",
            showIf: `options.get('layoutType') !== 'Text Only'`,
        },
        {
            friendlyName: "Mobile Image",
            name: "mobileImage",
            type: "object",
            helperText: "Optional. 1:1 Aspect Ratio recommended for mobile.",
            showIf: `options.get('layoutType') !== 'Text Only'`,
            subFields: [
                {
                    friendlyName: "Source",
                    name: "src",
                    type: "file",
                    allowedFileTypes: ["jpg", "webm", "gif", "png"],
                },
                {
                    friendlyName: "Alternate Text",
                    name: "alt",
                    type: "text",
                },
            ],
        },
        {
            friendlyName: "Mobile Video",
            helperText: "Optional. This overrides the mobile image on mobile devices.",
            name: "mobileVideoSrc",
            type: "file",
            allowedFileTypes: ["mov", "webm", "mp4"],
            showIf: `options.get('layoutType') !== 'Text Only'`,
            onChange: uploadToMuxMobile,
        },
        {
            friendlyName: "Mobile Video (Mux)",
            helperText: "Mux Video ID for mobile.",
            name: "muxIdMobile",
            hidden: true,
            type: "text",
            showIf: `options.get('layoutType') !== 'Text Only'`,
        },
        {
            friendlyName: "Show Mute Toggle",
            name: "showMuteToggle",
            type: "boolean",
            defaultValue: false,
            helperText:
                "Shows a mute/unmute button on videos in this section. Has no effect when only images are used.",
        },
        {
            // This should only be for 2col layout type with media and text
            friendlyName: "Extra Media",
            name: "additionalMedia",
            type: "list",
            showIf: `options.get('layoutType') === 'Media and Text' && options.get('columns') === 'Double'`,
            subFields: [
                {
                    friendlyName: "Image",
                    helperText: "Any Aspect Ratio. Uploading a video will replace this image.",
                    name: "src",
                    type: "file",
                    allowedFileTypes: ["jpg", "webm", "gif", "png"],
                },
                {
                    friendlyName: "Alternate Text",
                    name: "alt",
                    type: "text",
                },
                {
                    friendlyName: "Video",
                    helperText: "Any aspect ratio. This overrides the image, for this item",
                    name: "videoSrc",
                    type: "file",
                    allowedFileTypes: ["mov", "webm", "mp4"],
                },
            ],
        },
        {
            // This should only be for layoutType Media and Text
            // This should only be for two column layouts
            friendlyName: "Main Media Horizontal Alignment",
            name: "mediaPositionHorizontal",
            type: "text",
            helperText: "Applies to tablet and larger viewports only",
            enum: Object.values(EMediaAndTextMediaPositionHorizontal),
            defaultValue: EMediaAndTextMediaPositionHorizontal.RIGHT,
            showIf: `options.get('layoutType') === 'Media and Text' && options.get('columns') === 'Double'`,
        },
        {
            // This should only be for layoutType Media and Text
            // This should only be for one column layouts
            friendlyName: "Main Media Vertical Alignment",
            name: "mediaPositionVertical",
            type: "text",
            helperText: "Applies to tablet and larger viewports only",
            enum: Object.values(EMediaAndTextMediaPositionVertical),
            defaultValue: EMediaAndTextMediaPositionVertical.BOTTOM,
            showIf: `options.get('layoutType') === 'Media and Text' && options.get('columns') === 'Single'`,
        },
        {
            // This should only be for layout type with media ONLY
            // This should only be for two column layouts
            friendlyName: "Secondary Image",
            name: "secondaryImage",
            type: "object",
            helperText: "Any Aspect Ratio. Uploading a video will replace this image.",
            showIf: `options.get('layoutType') === 'Media Only' && options.get('columns') === 'Double'`,
            subFields: [
                {
                    friendlyName: "Source",
                    name: "src",
                    type: "file",
                    allowedFileTypes: ["jpg", "webm", "gif", "png"],
                },
                {
                    friendlyName: "Alternate Text",
                    name: "alt",
                    type: "text",
                },
            ],
        },
        {
            // This should only be for layout type with media
            friendlyName: "Secondary Image Link (optional)",
            name: "secondaryImageLink",
            type: "text",
            showIf: `options.get('layoutType') === 'Media Only' && options.get('columns') === 'Double'`,
        },
        {
            // This should only be for layout type with media ONLY
            // This should only be for two column layouts
            friendlyName: "Secondary Video",
            helperText: "Any aspect ratio. This overrides the secondary image.",
            name: "secondaryVideoSrc",
            type: "file",
            allowedFileTypes: ["mov", "webm", "mp4"],
            showIf: `options.get('layoutType') === 'Media Only' && options.get('columns') === 'Double'`,
        },
        {
            friendlyName: "Background Colour",
            name: "backgroundColour",
            type: "color",
        },
        {
            // This should only be for layout type with text
            friendlyName: "Text Colour",
            name: "txtColour",
            type: "color",
            showIf: `options.get('layoutType') !== 'Media Only'`,
        },
    ],
};
